import { useState } from "react";
import { Link } from "react-router-dom";
import { TID } from "@/constants/testIds";
import { FaFacebook, FaInstagram, FaLinkedin, FaChrome, FaFirefoxBrowser } from "react-icons/fa";
import { ArrowUpRight, Lightning, CalendarBlank, Broadcast, Sparkle, GlobeHemisphereWest, PaperPlaneTilt } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Landing() {
  const [loading, setLoading] = useState(false);

  const signIn = () => {
    setLoading(true);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#09090B] text-white">
      {/* ambient blobs */}
      <div className="blob" style={{ background: "#FF4500", width: 480, height: 480, top: -140, left: -160 }} />
      <div className="blob" style={{ background: "#D4FF00", width: 380, height: 380, top: 260, right: -120, opacity: 0.22 }} />
      <div className="grain absolute inset-0" />

      {/* Nav */}
      <header className="relative z-10 border-b border-white/5 backdrop-blur-xl bg-black/40">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF4500] flex items-center justify-center" style={{ borderRadius: 2 }}>
              <Broadcast size={18} weight="fill" color="#fff" />
            </div>
            <span className="font-display text-xl">clydeflow</span>
            <span className="overline ml-1">/ scheduler</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#A1A1AA]">
            <a href="#features" className="hover:text-white transition-colors duration-150">Features</a>
            <a href="#how" className="hover:text-white transition-colors duration-150">How it works</a>
            <a href="#install" className="hover:text-white transition-colors duration-150">Install</a>
          </nav>
          <button data-testid={TID.loginGoogleBtn} onClick={signIn} disabled={loading} className="btn btn-primary">
            {loading ? "redirecting…" : "sign in with google"} <ArrowUpRight size={16} weight="bold" />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-6 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="overline mb-6">clydeflow · social scheduler v0.1</div>
          <h1 className="font-display font-medium leading-[0.95] tracking-tighter text-[56px] md:text-[92px]">
            Grab any URL.<br />
            <span className="text-[#FF4500]">Blast it</span> to your socials —<br />
            <span className="text-[#D4FF00]">now</span> or later.
          </h1>
          <p className="mt-8 max-w-[560px] text-[#A1A1AA] text-lg leading-relaxed">
            A tiny browser extension that lives in your toolbar. Land on a page, click the icon,
            pick platforms, hit post. Or schedule it — ClydeFlow queues it and fires when the clock hits.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <button data-testid={TID.heroCta} onClick={signIn} className="btn btn-primary">
              <PaperPlaneTilt size={16} weight="fill" /> Start free with Google
            </button>
            <a href="#install" className="btn btn-ghost">
              <FaChrome /> Get the extension
            </a>
          </div>
          <div className="mt-10 flex items-center gap-4 text-[#71717A] text-sm">
            <span className="chip"><Lightning size={12} weight="fill" /> instant post</span>
            <span className="chip"><CalendarBlank size={12} weight="fill" /> smart scheduling</span>
            <span className="chip"><GlobeHemisphereWest size={12} weight="fill" /> works on any page</span>
          </div>
        </div>

        {/* Extension mockup */}
        <div className="lg:col-span-5 relative">
          <div className="relative mx-auto" style={{ width: 380, height: 520 }}>
            <div className="absolute -inset-3 bg-[#FF4500]/20 blur-2xl" />
            <div className="relative bg-[#18181B] border border-[#3F3F46] rounded-xl p-4 h-full flex flex-col shadow-[0_20px_60px_-15px_rgba(0,0,0,0.9)]">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-[#FF4500]" style={{ borderRadius: 2 }} />
                  <span className="font-display">clydeflow</span>
                </div>
                <Sparkle size={16} className="text-[#D4FF00]" weight="fill" />
              </div>
              <div className="overline mb-2">current tab</div>
              <div className="border border-[#27272A] p-3 mb-3 bg-black/40" style={{ borderRadius: 2 }}>
                <div className="text-xs text-[#71717A] font-mono truncate">https://clydeflow.com/pricing</div>
                <div className="mt-2 text-sm font-medium">ClydeFlow — Pricing plans that scale</div>
                <div className="mt-1 text-xs text-[#A1A1AA]">Simple pricing. Post to every network from one place.</div>
              </div>
              <div className="overline mb-2">caption</div>
              <div className="border border-[#27272A] p-3 mb-3 bg-black/40 text-sm text-[#A1A1AA]" style={{ borderRadius: 2, minHeight: 74 }}>
                Just shipped our new pricing! Check it out ↓
              </div>
              <div className="overline mb-2">platforms</div>
              <div className="flex gap-2 mb-4">
                <span className="chip" style={{ borderColor: "#1877F2", color: "#93c5fd" }}><FaFacebook /> facebook</span>
                <span className="chip" style={{ borderColor: "#E4405F", color: "#fecaca" }}><FaInstagram /> instagram</span>
                <span className="chip" style={{ borderColor: "#0A66C2", color: "#93c5fd" }}><FaLinkedin /> linkedin</span>
              </div>
              <div className="mt-auto grid grid-cols-2 gap-2">
                <button className="btn btn-primary text-sm">post now</button>
                <button className="btn btn-secondary text-sm">schedule</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bento features */}
      <section id="features" className="relative z-10 max-w-[1400px] mx-auto px-6 py-16 border-t border-[#27272A]">
        <div className="overline mb-6">the stack</div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-7 border border-[#27272A] p-8 bg-[#0f0f10] relative overflow-hidden" style={{ borderRadius: 2 }}>
            <div className="overline mb-3">01 · one-click capture</div>
            <div className="font-display text-3xl md:text-4xl mb-3 max-w-xl">Landing on any page? The extension already has the URL.</div>
            <div className="text-[#A1A1AA] max-w-lg">
              No copy-pasting. No opening a dashboard. Click the toolbar icon, ClydeFlow reads the tab&apos;s URL and title, ready to share.
            </div>
            <div className="absolute bottom-4 right-6 font-display text-[120px] text-[#27272A] leading-none select-none">→</div>
          </div>
          <div className="md:col-span-5 border border-[#27272A] p-8 bg-[#0f0f10]" style={{ borderRadius: 2 }}>
            <div className="overline mb-3">02 · pick your networks</div>
            <div className="font-display text-2xl mb-3">Facebook. Instagram. LinkedIn.</div>
            <div className="flex gap-3 flex-wrap">
              <div className="chip" style={{ borderColor: "#1877F2", color: "#93c5fd" }}><FaFacebook /> facebook</div>
              <div className="chip" style={{ borderColor: "#E4405F", color: "#fecaca" }}><FaInstagram /> instagram</div>
              <div className="chip" style={{ borderColor: "#0A66C2", color: "#93c5fd" }}><FaLinkedin /> linkedin</div>
            </div>
            <div className="text-[#A1A1AA] mt-4 text-sm">Multi-select. Each platform posts through the ClydeFlow relay.</div>
          </div>
          <div className="md:col-span-5 border border-[#27272A] p-8 bg-[#0f0f10]" style={{ borderRadius: 2 }}>
            <div className="overline mb-3">03 · schedule anywhere</div>
            <div className="font-display text-2xl mb-3">Fire now, or set a time.</div>
            <div className="text-[#A1A1AA] text-sm">Our worker runs every 15 seconds. If your slot&apos;s up, ClydeFlow pushes it out. No cron. No sweat.</div>
          </div>
          <div className="md:col-span-7 border border-[#27272A] p-8 bg-gradient-to-br from-[#18181B] to-[#0f0f10] relative overflow-hidden" style={{ borderRadius: 2 }}>
            <div className="overline mb-3">04 · your posting log, live</div>
            <div className="font-display text-3xl md:text-4xl mb-3">See what fired. And what failed.</div>
            <div className="text-[#A1A1AA] max-w-lg">A dense dashboard of every queued, scheduled, posted, and failed drop. Retry with a click.</div>
            <div className="mt-6 flex gap-2 flex-wrap">
              <span className="status-pill status-queued">queued</span>
              <span className="status-pill status-scheduled">scheduled</span>
              <span className="status-pill status-posted">posted</span>
              <span className="status-pill status-failed">failed</span>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="relative z-10 max-w-[1400px] mx-auto px-6 py-16 border-t border-[#27272A]">
        <div className="overline mb-3">how it works</div>
        <div className="font-display text-4xl md:text-5xl max-w-3xl">Four steps. One toolbar icon. Zero context switching.</div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { n: "01", t: "Install the extension", d: "Grab it for Chrome or Firefox. Pin it to your toolbar." },
            { n: "02", t: "Sign in with Google", d: "Emergent Google login. One click, no passwords." },
            { n: "03", t: "Connect socials", d: "Toggle Facebook, Instagram, LinkedIn from the dashboard." },
            { n: "04", t: "Land, click, post — track clicks", d: "Any URL. Any time. Or set the clock. Every link auto-shortens and counts clicks per platform." },
          ].map((s) => (
            <div key={s.n} className="border border-[#27272A] p-6 bg-[#0f0f10] hover:border-[#3F3F46] transition-colors duration-200" style={{ borderRadius: 2 }}>
              <div className="font-mono text-[#71717A] text-xs mb-3">{s.n}</div>
              <div className="font-display text-xl mb-2">{s.t}</div>
              <div className="text-[#A1A1AA] text-sm">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Install */}
      <section id="install" className="relative z-10 max-w-[1400px] mx-auto px-6 py-20 border-t border-[#27272A]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="overline mb-3">get the extension</div>
            <div className="font-display text-4xl md:text-5xl mb-4">Ready in 20 seconds.</div>
            <div className="text-[#A1A1AA] max-w-md">Download the packaged extension and load it in your browser. Sign in with Google, connect your accounts, and start shipping.</div>
          </div>
          <div className="flex flex-col gap-3">
            <a data-testid={TID.downloadChromeBtn} href="/extension/clydeflow-extension.zip" className="btn btn-primary justify-between px-6 py-4">
              <span className="flex items-center gap-3"><FaChrome size={20} /> download for chrome / edge / brave</span>
              <ArrowUpRight />
            </a>
            <a data-testid={TID.downloadFirefoxBtn} href="/extension/clydeflow-extension.zip" className="btn btn-ghost justify-between px-6 py-4">
              <span className="flex items-center gap-3"><FaFirefoxBrowser size={20} /> download for firefox</span>
              <ArrowUpRight />
            </a>
            <Link to="/extension-popup" className="text-sm text-[#71717A] hover:text-[#D4FF00] mt-2 underline underline-offset-4 transition-colors duration-150">
              or preview the extension popup in your browser →
            </Link>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-[#27272A] py-8">
        <div className="max-w-[1400px] mx-auto px-6 flex justify-between text-[#71717A] text-sm">
          <div>© 2026 clydeflow · social scheduler</div>
          <div className="font-mono">built for shipping fast</div>
        </div>
      </footer>
    </div>
  );
}
